import { getCultureFitVideoId, getInterviewVideoId } from './module-identificators';
import { getInternetSpeed } from './internet-quality';

// could be either video or image
export function preloadResource(resourceUrl) {
    return new Promise((resolve, reject) => {
        const request = new XMLHttpRequest();
        request.open('GET', resourceUrl, true);
        request.responseType = 'blob';
        // Without a timeout the XHR can stall indefinitely on flaky networks
        // (proxies that don't return a status, dropped connections, etc.),
        // which previously left the preloader hanging forever and the user
        // stuck on a loading spinner with no error logged. 8s is plenty for
        // any healthy network to start producing data; if the request hasn't
        // delivered by then, falling back is better than waiting longer.
        request.timeout = 8000;

        request.onload = (data) => {
            const status = data.target.status;
            if (status >= 200 && status < 300) {
                // Explicitly re-wrap the response with the correct MIME type.
                // iOS 18.7+ WebKit has a stricter blob-URL media decoder that
                // rejects blobs whose type doesn't match the actual content,
                // even when the server sent the correct Content-Type header.
                // Re-creating the Blob with an explicit type ensures the
                // video element always sees 'video/mp4'.
                const raw = data.target.response;
                const type =
                    raw.type && raw.type.startsWith('video/')
                        ? raw.type
                        : 'video/mp4';
                resolve(new Blob([raw], { type }));
                return;
            }
            // Any non-2xx status (304/403/404/5xx, captive portals, etc.)
            // must reject so callers don't await a promise that never settles.
            reject(
                new Error(
                    `preloadResource - HTTP ${status} for ${resourceUrl}`,
                ),
            );
        };

        request.onerror = (error) => {
            reject(error);
        };

        request.ontimeout = () => {
            reject(
                new Error(
                    `preloadResource - timeout after ${request.timeout}ms for ${resourceUrl}`,
                ),
            );
        };

        request.onabort = () => {
            reject(
                new Error(`preloadResource - aborted for ${resourceUrl}`),
            );
        };

        request.send();
    });
}

export async function getVideosToPreload(modules) {
    let resolution;
    try {
        const internet = await getInternetSpeed();
        resolution = +internet.mbps < 100 ? 'low_res' : 'high_res';
    } catch (error) {
        resolution = 'high_res';
    }

    const getVideosFromCultureFit = () => {
        const cultureFitModule = modules.find((module) => module.type === 'culture-fit-video');

        if (cultureFitModule) {
            return [
                { videoId: getCultureFitVideoId(), videoUrl: cultureFitModule.videos[resolution] },
            ];
        } else return [];
    };

    const getVideosFromInterviewModule = () => {
        const interviewModule = modules.find((module) => module.type === 'interview-module');

        if (interviewModule) {
            const questionsWithVideo = [...interviewModule.questions].filter(
                (question) => question.videos
            );
            return questionsWithVideo.map((question) => ({
                videoId: getInterviewVideoId(question.questionId),
                videoUrl: question.videos[resolution],
            }));
        } else return [];
    };

    const cultureFitVideos = getVideosFromCultureFit();
    const interviewModuleVideos = getVideosFromInterviewModule();

    return [...cultureFitVideos, ...interviewModuleVideos];
}
